import * as zExt from "@redotech/rpc/ext";
import * as z from "zod/v4";

import {
  convertShopifyIdToGid,
  ShopifyIdType,
} from "@redotech/shopify-util/id";
import { getFullName } from "@redotech/string/name";
import {
  CheckoutLineItem,
  MailingAddress,
  PixelEventsCheckoutStarted,
  ProductVariant as ShopifyProductVariant,
} from "@shopify/web-pixels-extension/build/ts/types/PixelEvents";
import {
  parseCheckoutPrice,
  ShopifyCheckoutWebhook,
  CheckoutLineItem as WebhookCheckoutLineItem,
} from "../../shopify-checkout-webhook";

////////////////////////////
/////// Redo types /////////
////////////////////////////

export const redoStorefrontEventMoneySchema = z.object({
  amount: zExt.coerceNull(z.number()),
  currencyCode: zExt.coerceNull(z.string()),
});

export const redoStorefrontEventProductSchema = z.object({
  parentProduct: zExt.coerceNull(
    z.object({
      productId: zExt.coerceNull(z.string()),
      title: zExt.coerceNull(z.string()),
      url: zExt.coerceNull(z.string()),
    }),
  ),
  isRedoProduct: z
    .boolean()
    .nullish()
    .transform((val) => val ?? false),
  variantId: zExt.coerceNull(z.string()),
  title: zExt.coerceNull(z.string()),
  sku: zExt.coerceNull(z.string()),
  imageUrl: zExt.coerceNull(z.string()),
  price: zExt.coerceNull(redoStorefrontEventMoneySchema),
});

export const redoCartLineSchema = z.object({
  ...redoStorefrontEventProductSchema.shape,
  quantity: zExt.coerceNull(z.number()),
});
export type RedoStorefrontEventCartLine = z.infer<typeof redoCartLineSchema>;

/** Like ExtendedAddress, but all fields are nullable, and
 * missing fields are converted to null when parsing */
const redoCheckoutAddressSchema = z.object({
  street1: zExt.coerceNull(z.string()),
  street2: zExt.coerceNull(z.string()),
  city: zExt.coerceNull(z.string()),
  state: zExt.coerceNull(z.string()),
  postalCode: zExt.coerceNull(z.string()),
  country: zExt.coerceNull(z.string()),

  name: zExt.coerceNull(z.string()),
  firstName: zExt.coerceNull(z.string().optional()),
  lastName: zExt.coerceNull(z.string().optional()),

  phone: zExt.coerceNull(z.string().optional()),
  email: zExt.coerceNull(z.string().optional()),
  company: zExt.coerceNull(z.string().optional()),
});
type RedoCheckoutAddress = z.infer<typeof redoCheckoutAddressSchema>;

export const redoCheckoutInfoSchema = z.object({
  cartId: zExt.coerceNull(z.string()),
  cartLines: zExt.coerceNull(z.array(redoCartLineSchema)),
  shippingAddress: zExt.coerceNull(redoCheckoutAddressSchema),
  billingAddress: zExt.coerceNull(redoCheckoutAddressSchema),

  subtotal: zExt.coerceNull(redoStorefrontEventMoneySchema),
  tax: zExt.coerceNull(redoStorefrontEventMoneySchema),
  shipping: zExt.coerceNull(redoStorefrontEventMoneySchema),
  totalPrice: zExt.coerceNull(redoStorefrontEventMoneySchema),
});

///////////////////////////
///// Shopify types ///////
///////////////////////////

export function shopifyProductVariantToRedoProductVariant(
  productVariant: ShopifyProductVariant,
): z.infer<typeof redoStorefrontEventProductSchema> {
  return {
    parentProduct: {
      productId: productVariant.product.id,
      title: productVariant.product.untranslatedTitle,
      url: productVariant.product.url,
    },
    isRedoProduct: productVariant.product.vendor === "re:do",
    variantId: productVariant.id,
    title: productVariant.untranslatedTitle,
    sku: productVariant.sku,
    imageUrl: productVariant.image?.src ?? null,
    price: {
      amount: productVariant.price.amount,
      currencyCode: productVariant.price.currencyCode,
    },
  };
}

/**
 * @see _ https://shopify.dev/docs/api/web-pixels-api/standard-events/product_added_to_cart
 * @see _ https://community.shopify.com/t/we-are-receiving-null-and-undefined-as-the-values-for-fields-from-pixel-event/363753
 *
 * Sometimes most fields are null, undefined, or missing.
 */
export const shopifyCartLineSchema_ = z.object({
  cost: z.object({
    totalAmount: z.object({
      amount: z.number().nullable(),
      currencyCode: z.string(),
    }),
  }),
  quantity: z.number().nullish(),
  merchandise: z.object({
    id: z.string().nullish(), // yes, actually nullable
    title: z.string().nullish(),
    product: z.object({
      id: z.string().nullish(), // yes, actually nullable
      title: z.string().nullish(),
      type: z.string().nullish(),
      untranslatedTitle: z.string().nullish(),
      url: z.string().nullish(),
      vendor: z.string().nullish(),
    }),
    image: z.object({ src: z.string().nullish() }).nullish(),
    price: z.object({
      amount: z.number().optional(),
      currencyCode: z.string(),
    }),
    sku: z.string().nullish(),
    untranslatedTitle: z.string().nullish(),
  }),
});
export interface ShopifyCartLine
  extends z.infer<typeof shopifyCartLineSchema_> {}
export const shopifyCartLineSchema: z.ZodType<ShopifyCartLine> =
  shopifyCartLineSchema_;

/**
 * Shopify claims to potentially give us all of this information, but in practice
 * it's just the cart ID, so we have to fill in the rest by fetching the cart again.
 *
 * However, since Shopify doesn't make any guarantees about what they send us,
 * let's keep the full schema here in case that ever changes.
 */
const shopifyPixelEventCartSchema_ = z.object({
  id: z.string().nullish(),
  cost: z
    .object({
      totalAmount: z.object({ amount: z.number(), currencyCode: z.string() }),
    })
    .nullish(),
  totalQuantity: z.number().nullish(),
  lines: z.array(shopifyCartLineSchema).nullish(),
});
export interface ShopifyPixelEventCart
  extends z.infer<typeof shopifyPixelEventCartSchema_> {}
export const shopifyPixelEventCartSchema: z.ZodType<ShopifyPixelEventCart> =
  shopifyPixelEventCartSchema_;

export function shopifyCartLineToRedoProductVariant(
  cartLine: z.infer<typeof shopifyCartLineSchema> | null | undefined,
): z.infer<typeof redoStorefrontEventProductSchema> {
  return {
    parentProduct: {
      productId: cartLine?.merchandise.product.id ?? null,
      title: cartLine?.merchandise.product.title ?? null,
      url: cartLine?.merchandise.product.url ?? null,
    },
    isRedoProduct: cartLine?.merchandise.product.vendor === "re:do",
    variantId: cartLine?.merchandise.id ?? null,
    title: cartLine?.merchandise.title ?? null,
    sku: cartLine?.merchandise.sku ?? null,
    imageUrl: cartLine?.merchandise.image?.src ?? null,
    price: {
      amount: cartLine?.merchandise.price.amount ?? null,
      currencyCode: cartLine?.merchandise.price.currencyCode ?? null,
    },
  };
}
export function shopifyCartLineToRedoCartLine(
  cartLine: z.infer<typeof shopifyCartLineSchema> | null | undefined,
): z.infer<typeof redoCartLineSchema> {
  return {
    ...shopifyCartLineToRedoProductVariant(cartLine),
    quantity: cartLine?.quantity ?? null,
  };
}
export function shopifyCheckoutLineItemToRedoCartLine(
  checkoutLineItem: CheckoutLineItem,
): z.infer<typeof redoCartLineSchema> {
  return {
    parentProduct: {
      productId: checkoutLineItem?.variant?.product.id ?? null,
      title: checkoutLineItem?.variant?.product.title ?? null,
      url: checkoutLineItem?.variant?.product.url ?? null,
    },
    isRedoProduct: checkoutLineItem?.variant?.product.vendor === "re:do",
    variantId: checkoutLineItem?.variant?.id ?? null,
    title: checkoutLineItem?.variant?.title ?? null,
    sku: checkoutLineItem?.variant?.sku ?? null,
    imageUrl: checkoutLineItem?.variant?.image?.src ?? null,
    price: {
      amount: checkoutLineItem?.variant?.price.amount ?? null,
      currencyCode: checkoutLineItem?.variant?.price.currencyCode ?? null,
    },
    quantity: checkoutLineItem.quantity,
  };
}

export function shopifyWebhookLineItemToRedoCartLine(
  lineItem: WebhookCheckoutLineItem,
  currency: string | null | undefined,
): z.infer<typeof redoCartLineSchema> {
  return {
    parentProduct: {
      productId: convertShopifyIdToGid(
        lineItem.product_id.toString(),
        ShopifyIdType.Product,
      ),
      title: lineItem.title,
      url: null,
    },
    isRedoProduct: lineItem.vendor === "re:do",
    variantId: convertShopifyIdToGid(
      lineItem.variant_id.toString(),
      ShopifyIdType.ProductVariant,
    ),
    title: lineItem.variant_title ?? lineItem.title,
    sku: lineItem.sku ?? null,
    imageUrl: null,
    price: {
      amount: parseCheckoutPrice(lineItem.price),
      currencyCode: currency ?? null,
    },
    quantity: lineItem.quantity,
  };
}

// TECH-DEBT -- we should really parse these fields using parsers instead
// of using `z.any()` followed by casting. Feel free to clean up.
export const shopifyCheckoutSchema = z.any().transform(
  (val: any) =>
    val as Omit<
      PixelEventsCheckoutStarted["data"]["checkout"],
      "lineItems" | "subtotalPrice" | "totalPrice" | "totalTax" | "shippingLine"
    > & {
      // don't trust shopify types - sometimes their events omit lineItems
      lineItems?: PixelEventsCheckoutStarted["data"]["checkout"]["lineItems"];
      subtotalPrice:
        | PixelEventsCheckoutStarted["data"]["checkout"]["subtotalPrice"]
        | null;
      totalPrice:
        | PixelEventsCheckoutStarted["data"]["checkout"]["totalPrice"]
        | null;
      totalTax:
        | PixelEventsCheckoutStarted["data"]["checkout"]["totalTax"]
        | null;
      shippingLine:
        | PixelEventsCheckoutStarted["data"]["checkout"]["shippingLine"]
        | null;
    },
);
export type RobustCheckout = z.infer<typeof shopifyCheckoutSchema>;

function shopifyMailingAddressToRedoAddress(
  mailingAddress: MailingAddress,
): RedoCheckoutAddress {
  return {
    street1: mailingAddress.address1,
    street2: mailingAddress.address2,
    city: mailingAddress.city,
    state: mailingAddress.province,
    country: mailingAddress.country,
    postalCode: mailingAddress.zip,

    name:
      getFullName(mailingAddress.firstName, mailingAddress.lastName) || null,
    firstName: mailingAddress.firstName,
    lastName: mailingAddress.lastName,

    phone: mailingAddress.phone,
    email: null,
    company: null,
  };
}

function shopifyWebhookAddressToRedoAddress(
  address: NonNullable<ShopifyCheckoutWebhook["shipping_address"]>,
): RedoCheckoutAddress {
  return {
    street1: address.address1 ?? null,
    street2: address.address2 ?? null,
    city: address.city ?? null,
    state: address.province ?? null,
    postalCode: address.zip ?? null,
    country: address.country ?? null,
    name:
      [address.first_name, address.last_name].filter(Boolean).join(" ") || null,
    firstName: address.first_name ?? null,
    lastName: address.last_name ?? null,
    phone: address.phone ?? null,
    email: null,
    company: address.company ?? null,
  };
}

function isWebhookCheckout(
  checkout: RobustCheckout | ShopifyCheckoutWebhook | null | undefined,
): checkout is ShopifyCheckoutWebhook {
  if (!checkout) {
    return false;
  }
  if ("_type" in checkout && checkout._type === "webhook") {
    return true;
  }
  return false;
}

export function shopifyCheckoutToRedoCheckout({
  checkout,
  cartId,
}: {
  checkout: RobustCheckout | ShopifyCheckoutWebhook | null | undefined;
  cartId: string | null;
}): z.infer<typeof redoCheckoutInfoSchema> {
  if (!checkout) {
    return {
      cartId,
      cartLines: null,
      subtotal: null,
      tax: null,
      shipping: null,
      totalPrice: null,
      shippingAddress: null,
      billingAddress: null,
    };
  }

  if (isWebhookCheckout(checkout)) {
    const shippingLine = checkout.shipping_lines?.[0];
    return {
      cartId: cartId ?? checkout.cart_token ?? null,
      cartLines:
        checkout.line_items?.map((item) =>
          shopifyWebhookLineItemToRedoCartLine(item, checkout.currency),
        ) ?? null,
      subtotal: {
        amount: parseCheckoutPrice(checkout.subtotal_price),
        currencyCode: checkout.currency ?? null,
      },
      tax: {
        amount: parseCheckoutPrice(checkout.total_tax),
        currencyCode: checkout.currency ?? null,
      },
      shipping: shippingLine
        ? {
            amount: parseCheckoutPrice(shippingLine.price),
            currencyCode: checkout.currency ?? null,
          }
        : null,
      totalPrice: {
        amount: parseCheckoutPrice(checkout.total_price),
        currencyCode: checkout.currency ?? null,
      },
      shippingAddress: checkout.shipping_address
        ? shopifyWebhookAddressToRedoAddress(checkout.shipping_address)
        : null,
      billingAddress: checkout.billing_address
        ? shopifyWebhookAddressToRedoAddress(checkout.billing_address)
        : null,
    };
  }

  return {
    cartId,
    cartLines:
      checkout.lineItems?.map(shopifyCheckoutLineItemToRedoCartLine) ?? null,
    subtotal: checkout.subtotalPrice
      ? {
          amount: checkout.subtotalPrice.amount,
          currencyCode: checkout.subtotalPrice.currencyCode,
        }
      : null,
    tax: checkout.totalTax
      ? {
          amount: checkout.totalTax.amount,
          currencyCode: checkout.totalTax.currencyCode,
        }
      : null,
    shipping: checkout.shippingLine
      ? {
          amount: checkout.shippingLine.price.amount ?? null,
          currencyCode: checkout.shippingLine.price.currencyCode ?? null,
        }
      : null,
    totalPrice: checkout.totalPrice
      ? {
          amount: checkout.totalPrice.amount,
          currencyCode: checkout.totalPrice.currencyCode,
        }
      : null,
    shippingAddress: checkout.shippingAddress
      ? shopifyMailingAddressToRedoAddress(checkout.shippingAddress)
      : null,
    billingAddress: checkout.billingAddress
      ? shopifyMailingAddressToRedoAddress(checkout.billingAddress)
      : null,
  };
}

///////////////////////////
//// CommentSold types ////
///////////////////////////

/**
 * Convert CommentSold LineItem to Redo cart line format
 * Note: CommentSold sends prices in pennies, so we divide by 100
 */
export function commentSoldLineItemToRedoCartLine(
  lineItem: {
    variantId: number;
    productId: number;
    name: string;
    sku: string | null;
    mainImageUrl: string | null;
    attr1Name: string;
    attr2Name: string;
    attr1: string;
    attr2: string;
    price: { amount: number; currencyCode: string };
    originalPrice: { amount: number; currencyCode: string };
  },
  quantity: number,
): z.infer<typeof redoCartLineSchema> {
  return {
    parentProduct: {
      productId: String(lineItem.productId),
      title: lineItem.name,
      url: null,
    },
    isRedoProduct: false, // TODO: when would this be true?
    variantId: String(lineItem.variantId),
    title: lineItem.name,
    sku: lineItem.sku,
    imageUrl: lineItem.mainImageUrl,
    price: {
      amount: lineItem.price.amount / 100,
      currencyCode: lineItem.price.currencyCode,
    },
    quantity,
  };
}

/**
 * Convert CommentSold product variant to Redo product format
 * Note: CommentSold sends prices in pennies, so we divide by 100
 */
export function commentSoldProductVariantToRedoProduct(
  variant: {
    id: number;
    attr1: string;
    attr2: string;
    price: { amount: number; currencyCode: string };
    originalPrice: { amount: number; currencyCode: string };
    availableQuantity: number;
  },
  product: {
    id: number;
    name: string;
    sku: string | null;
    mainImageUrl: string | null;
    attr1Name: string;
    attr2Name: string;
  },
  url: string,
): z.infer<typeof redoStorefrontEventProductSchema> {
  return {
    parentProduct: { productId: String(product.id), title: product.name, url },
    isRedoProduct: false,
    variantId: String(variant.id),
    title: product.name,
    sku: product.sku,
    imageUrl: product.mainImageUrl,
    price: {
      amount: variant.price.amount / 100,
      currencyCode: variant.price.currencyCode,
    },
  };
}
